// Import the RTK Query methods from the React-specific entry point
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';

import { urlifyRouteIds } from 'state/apiSlice';

import { Point } from 'geojson';

import { MetricsRouteData, RouteInfo } from '../appTypes';
import { baseQuery } from './utils';

// https://redux.js.org/tutorials/essentials/part-7-rtk-query-basics
// https://redux-toolkit.js.org/rtk-query/usage/prefetching

type CreateSavedRouteArgs = {
  project_slug: string;
  savedRoute: {
    routeName: string;
    routeSegmentIds: string[];
    realizedSegments: string[];
  };
};

export type SavedRoute = {
  id: string;
  routeName: string;
  routeSegmentIds: string[];
  realizedSegments: string[];
  starred: boolean;
  creatorEmail?: string | null;
  centerPoint?: Point;
  had_issues_crosswalking?: boolean;
  before_id?: string;
  after_id?: string;
  position?: number;
  alerts?: string[];
};
export type SavedRouteList = SavedRoute[];
export type SavedRouteAndFeature = {
  savedRoute: SavedRoute;
  feature: RouteInfo;
  id: string;
};
export type TempRoute = {
  id: string;
  routeName?: string;
  cardinal_direction?: string;
  route_length?: number;
};

export type MonitoredRoute = {
  conditionScore: number;
  hasAlertEvent: boolean;
  avgTraveltime?: number;
  avgTypicalTraveltime?: number;
  maxTraveltime?: number;
  maxTraveltime_typ?: number;
  congestedTimeMinutes?: number;
  lastUpdated?: string;
  traveltime?: number;
  typicalTraveltime?: number;
};

export type MonitoredRouteList = Array<
  {
    id: string;
    routeName: string;
    routeSegmentIds: string[];
    realizedSegments: string[];
  } & MonitoredRoute
>;

type RouteStats = {
  [id: string]: MonitoredRoute;
};

const transformResponseStats = (
  rawResult: any[],
): Array<[string, MonitoredRoute]> => {
  return rawResult.map((el) => {
    const {
      avg_traveltime,
      avg_typical_traveltime,
      condition_score,
      has_alert_event,
      congested_time_minutes,
      id,
      max_traveltime,
      max_traveltime_typ,
      typical_traveltime,
      traveltime,
      last_updated,
    } = el;
    return [
      id,
      {
        avgTraveltime: avg_traveltime,
        avgTypicalTravelTime: avg_typical_traveltime,
        conditionScore: condition_score,
        hasAlertEvent: has_alert_event,
        congestedTime:
          congested_time_minutes != null
            ? Number(congested_time_minutes) * 60
            : undefined,
        maxTraveltime: max_traveltime,
        maxTraveltimeTyp: max_traveltime_typ,
        traveltime,
        typicalTraveltime: typical_traveltime,
        lastUpdated: last_updated,
      },
    ];
  });
};

const transformResponseTempRouteStats = (rawResult: any[]): MonitoredRoute => {
  return transformResponseStats(rawResult)[0][1];
};

const transformResponseRouteStats = (rawResults: any[]): RouteStats => {
  return Object.fromEntries(transformResponseStats(rawResults));
};

const transformResponseRoutes = (
  rawResult: any,
  meta,
  arg,
): { savedRoutes: SavedRouteList } => {
  return {
    savedRoutes: rawResult.saved_routes.map((serverSavedRoute) => {
      const { id, position, starred, alerts } = serverSavedRoute;
      return {
        id,
        routeName: serverSavedRoute.name,
        routeSegmentIds: serverSavedRoute.route_segment_ids,
        realizedSegments: serverSavedRoute.realized_segments,
        creatorEmail: serverSavedRoute.creator_email ?? null,
        position,
        starred,
        alerts,
      };
    }),
  };
};

const transformResponseRoute = (
  rawResult: any,
  meta,
  arg,
): SavedRouteAndFeature => {
  if (rawResult) {
    const { id, position, starred, had_issues_crosswalking } = rawResult;
    return {
      savedRoute: {
        id,
        routeName: rawResult.name,
        routeSegmentIds: rawResult.route_segment_ids,
        realizedSegments: rawResult.realized_segments,
        creatorEmail: rawResult.creator_email ?? null,
        centerPoint: rawResult.center_point,
        had_issues_crosswalking,
        position,
        starred,
      },
      feature: {
        type: 'Feature',
        geometry: rawResult.geometry,
        properties: {
          route_length: rawResult.route_length,
          cardinal_direction: rawResult.cardinal_direction,
        },
      },
      id: rawResult.id,
    };
  }
  return undefined;
};

type RouteId = {
  id: string;
};

const transformResponseRouteId = (rawResult: any, meta, arg): RouteId => {
  return { id: arg.savedRouteId };
};

const responseToRouteTag: any = (result, error, arg) => {
  if (!result) return [];
  return [{ type: 'savedRoute', id: result.id }];
};

const responseToRouteTagEdit: any = (result, error, arg) => {
  return [{ type: 'savedRoutes' }, { type: 'savedRoute', id: result.id }];
};

const initialState: {
  displayedSavedRoute?: SavedRoute;
  inSavedRouteEditMode: boolean;
  current: MonitoredRouteList;
  stats: RouteStats;
  lastUpdated: string;
} = {
  displayedSavedRoute: undefined,
  current: [],
  stats: undefined,
  inSavedRouteEditMode: false,
  lastUpdated: undefined,
};

export const savedRouteSlice = createSlice({
  name: 'savedRoutes',
  initialState,
  reducers: {
    resetSavedRoutes: (state) => {
      state.current = [];
      state.stats = undefined;
      state.lastUpdated = undefined;
      state.inSavedRouteEditMode = false;
      state.displayedSavedRoute = undefined;
    },
    setDisplayedSavedRoute: (state, action) => {
      state.displayedSavedRoute = action.payload;
    },
    setInSavedRouteEditMode: (state, action) => {
      state.inSavedRouteEditMode = action.payload;
    },
    setCurrentSavedRoute: (state, action) => {
      const index = state.current.findIndex(
        (item) => item.id === action.payload.id,
      );
      if (index !== -1) state.current[index] = action.payload;
    },
    setCurrentSavedRoutes: (state, action) => {
      state.current = action.payload;
    },
    setStatsSavedRoutes: (
      state,
      action: PayloadAction<RouteStats | undefined>,
    ) => {
      if (action.payload) {
        state.stats = { ...(state.stats ?? {}), ...action.payload };
        state.lastUpdated =
          Object.values(action.payload)[0]?.lastUpdated ?? undefined;
      } else {
        state.stats = undefined;
        state.lastUpdated = undefined;
      }
    },
    setTempStatsSavedRoutes: (
      state,
      action: PayloadAction<MonitoredRoute | undefined>,
    ) => {
      state.stats = { ...(state.stats ?? {}), temp: action.payload };
    },
    deleteCurrentSavedRoute: (state, action) => {
      state.current = state.current.filter(
        (route) => route.id !== action.payload,
      );
    },
  },
});
const { actions } = savedRouteSlice;

export const {
  resetSavedRoutes,
  setDisplayedSavedRoute,
  setInSavedRouteEditMode,
  setCurrentSavedRoutes,
  setCurrentSavedRoute,
  deleteCurrentSavedRoute,
  setStatsSavedRoutes,
  setTempStatsSavedRoutes,
} = actions;

export const savedRouteApiSlice = createApi({
  reducerPath: 'savedRouteApi',
  baseQuery,
  tagTypes: ['savedRoutes', 'rm', 'rmSegment', 'savedRouteAlerts'],
  endpoints: (builder) => ({
    getRouteMonitoring: builder.query<
      RouteStats,
      {
        project_slug: string;
        start_date: string;
        end_date: string;
        comparison: string;
        threshold: string;
      }
    >({
      query: ({
        project_slug,
        start_date,
        end_date,
        comparison,
        threshold,
      }) => {
        const queryString = `${project_slug}/route_monitoring/${start_date.replace('.000Z', '')}/${end_date.replace('.000Z', '')}/${comparison}/${threshold}/`;
        return queryString;
      },
      providesTags: [{ type: 'rm' }],
      transformResponse: (rawResult: any) => {
        return transformResponseRouteStats(rawResult);
      },
    }),
    getSegmentsMonitoring: builder.query<
      MonitoredRoute,
      {
        project_slug: string;
        start_date: string;
        end_date: string;
        comparison: string;
        threshold: string;
        segmentIds: Array<string>;
      }
    >({
      query: ({
        project_slug,
        start_date,
        end_date,
        comparison,
        threshold,
        segmentIds,
      }) => {
        const queryString = `${project_slug}/route_monitoring/${start_date.replace('.000Z', '')}/${end_date.replace('.000Z', '')}/${comparison}/${threshold}/segments/${urlifyRouteIds(
          segmentIds,
        )}`;
        return queryString;
      },
      transformResponse: (rawResult: any) => {
        return transformResponseTempRouteStats(rawResult);
      },
      providesTags: [{ type: 'rmSegment' }],
    }),
    getRouteMonitoringNow: builder.query<
      RouteStats,
      {
        project_slug: string;
        comparison: string;
        threshold: string;
      }
    >({
      query: ({ project_slug, comparison, threshold }) => {
        return `${project_slug}/route_monitoring/now/${comparison}/${threshold}/`;
      },
      transformResponse: (rawResult: any) => {
        return transformResponseRouteStats(rawResult);
      },
      providesTags: [{ type: 'rm' }],
    }),
    getSegmentsMonitoringNow: builder.query<
      MonitoredRoute,
      {
        project_slug: string;
        comparison: string;
        threshold: string;
        segmentIds: Array<string>;
      }
    >({
      query: ({ project_slug, comparison, threshold, segmentIds }) => {
        return `${project_slug}/route_monitoring/now/${comparison}/${threshold}/segments/${urlifyRouteIds(
          segmentIds,
        )}`;
      },
      transformResponse: (rawResult: any) => {
        return transformResponseTempRouteStats(rawResult);
      },
      providesTags: [{ type: 'rmSegment' }],
    }),
    // @ts-ignore
    getSavedRoutesWithGeojson: builder.query<
      SavedRouteAndFeature[],
      { project_slug: string; layerId: number }
    >({
      query: ({ project_slug, layerId }) => {
        return `${project_slug}/saved_routes_with_geojson/${layerId}/`;
      },
      transformResponse: (rawResult: any) => {
        return rawResult.map((serverSavedRoute) => {
          return transformResponseRoute(serverSavedRoute, undefined, undefined);
        });
      },
      providesTags: [{ type: 'savedRoutes' }, { type: 'rm' }],
    }),
    // @ts-ignore
    getSavedRoutes: builder.query<{ savedRoutes: SavedRouteList }, string>({
      query: (project_slug) => {
        return `${project_slug}/saved_routes/`;
      },
      transformResponse: transformResponseRoutes,
      providesTags: [{ type: 'savedRoutes' }],
    }),
    // @ts-ignore
    getSavedRoute: builder.query<
      SavedRouteAndFeature,
      { project_slug: string; savedRouteId: string; layerId?: number }
    >({
      query: ({ project_slug, savedRouteId, layerId }) => {
        let layerQuery = '';
        if (layerId) {
          layerQuery = `?layer=${layerId}`;
        }
        return `${project_slug}/saved_routes/${savedRouteId}/${layerQuery}`;
      },
      transformResponse: transformResponseRoute,
      providesTags: responseToRouteTag,
    }),
    // @ts-ignore
    savedRouteUpdate: builder.mutation<
      RouteId,
      {
        project_slug: string;
        savedRouteId: string;
        savedRoute: Partial<SavedRoute>;
      }
    >({
      query: ({ project_slug, savedRouteId, savedRoute }) => {
        return {
          url: `${project_slug}/saved_routes/${savedRouteId}/`,
          method: 'POST',
          body: {
            id: savedRouteId,
            name: savedRoute.routeName,
            route_segment_ids: savedRoute.routeSegmentIds,
            realized_segments: savedRoute.realizedSegments,
            position: savedRoute.position,
            starred: savedRoute.starred,
            before_id: savedRoute.before_id,
            after_id: savedRoute.after_id,
          },
        };
      },
      transformResponse: transformResponseRouteId,
      invalidatesTags: responseToRouteTagEdit,
    }),
    // @ts-ignore
    savedRouteDelete: builder.mutation<
      RouteId,
      { project_slug: string; savedRouteId: string }
    >({
      query: ({ project_slug, savedRouteId }) => {
        return {
          url: `${project_slug}/saved_routes/${savedRouteId}/`,
          method: 'DELETE',
        };
      },
      transformResponse: transformResponseRouteId,
      invalidatesTags: (result, error, { project_slug }) => [
        { type: 'savedRoutes' },
        // when removing a route, we remove also explicit alerts so we need to invalidate it as well
        { type: 'savedRouteAlerts', id: project_slug },
      ],
    }),
    // @ts-ignore
    savedRouteCreate: builder.mutation<RouteId, CreateSavedRouteArgs>({
      query: ({ project_slug, savedRoute }) => {
        return {
          url: `${project_slug}/saved_routes/`,
          method: 'PUT',
          body: {
            name: savedRoute.routeName,
            route_segment_ids: savedRoute.routeSegmentIds,
            realized_segments: savedRoute.realizedSegments,
          },
        };
      },
      transformResponse: (rawResult: any, meta, arg): RouteId => {
        return { id: rawResult.id };
      },
      invalidatesTags: [{ type: 'savedRoutes' }],
    }),
    getRouteTravelTimeBetweenDates: builder.query<
      { nrt: MetricsRouteData; typical: MetricsRouteData },
      {
        project_slug: string;
        start_date: string;
        end_date: string;
        typicalMonth: string;
        routeIds?: Array<string>;
        savedRouteId?: string;
      }
    >({
      query: ({
        project_slug,
        start_date,
        end_date,
        typicalMonth,
        routeIds,
        savedRouteId,
      }) => {
        if (savedRouteId) {
          return `${project_slug}/route_monitoring/traveltime/${start_date}/${end_date}/${typicalMonth}/${savedRouteId}/`;
        } else {
          return `${project_slug}/route_monitoring/traveltime/${start_date}/${end_date}/${typicalMonth}/segments/${urlifyRouteIds(
            routeIds,
          )}/`;
        }
      },
      transformResponse: (rawResult: any, meta, arg) => {
        return {
          nrt: {
            date: arg.start_date,
            routeIds: arg.routeIds,
            times: rawResult.nrt_times,
            bin_size: rawResult.bin_size,
            freeflow_traveltime: rawResult.freeflow_traveltime,
          },
          typical: {
            date: arg.start_date,
            routeIds: arg.routeIds,
            times: rawResult.typical_times,
            bin_size: rawResult.bin_size,
          },
        };
      },
    }),
  }),
});

export const {
  useGetSavedRoutesWithGeojsonQuery,
  useGetSavedRoutesQuery,
  useGetSavedRouteQuery,
  useSavedRouteUpdateMutation,
  useSavedRouteCreateMutation,
  useSavedRouteDeleteMutation,
  useGetRouteMonitoringNowQuery,
  useLazyGetRouteMonitoringQuery,
  useGetRouteMonitoringQuery,
  useGetRouteTravelTimeBetweenDatesQuery,
  useGetSegmentsMonitoringQuery,
  useGetSegmentsMonitoringNowQuery,
} = savedRouteApiSlice;

// Selectors
export const getSavedRouteState = (rootState) => {
  return rootState[savedRouteSlice.name];
};
export const selectDisplayedSavedRoute = (
  rootState,
): SavedRoute | undefined => {
  return getSavedRouteState(rootState).displayedSavedRoute;
};

export const selectInSavedRouteEditMode = (rootState): boolean => {
  return getSavedRouteState(rootState).inSavedRouteEditMode;
};

export const selectCurrentSavedRoutes = (
  rootState,
): SavedRouteAndFeature[] | undefined => {
  return getSavedRouteState(rootState).current;
};

export const selectStatsSavedRoutes = (rootState): RouteStats | undefined => {
  return getSavedRouteState(rootState).stats;
};
export const selectAreRoutesReady = (rootState): boolean => {
  const state = selectCurrentSavedRoutes(rootState);
  return state?.length > 0;
};

export const selectLastUpdated = (rootState): string => {
  return getSavedRouteState(rootState).lastUpdated;
};
